/* ============
 * Add multiple products modal
 * ============
 *
 * This is the component for creating batch log
 */
import { mapGetters, mapMutations } from 'vuex';
import organizationSettingsService from './../../services/organizationSettings';
import billService from './../../services/bill';
import paymentMethodService from './../../services/paymentMethods';
import Forms from './../../utils/forms/forms';
import moment from 'moment';

export default {
  data() {
    return {
      form: new Forms({
        paymentDateOption: {
          value: '',
          type: 'select',
        },
        paymentDate: {
          value: '',
          type: 'date',
        },
        paymentMethod: {
          value: '',
          type: 'select',
        },
      }),
      bills: [],
      showForm: true,
      loading: false,
      submitLoading: false,
      submitted: false,
      errors: [],
    };
  },
  props: {
    operation: {
      type: String,
      required: true,
      default: '',
    },
    selectedBills: {
      type: Array,
      required: true,
      default: () => [],
    },
    onSubmit: {
      type: Function,
      required: true,
      default: () => {},
    },
  },
  computed: {
    ...mapGetters({
      settings: 'organizationSettings',
      account: 'account',
      paymentMethods: 'paymentMethods',
    }),

    listPaymentMethods() {
      return this.paymentMethods.map(item => ({
        id: item.id,
        name: this.$t(`static.reports.bills.${item.name.replace(/\s+/g, '').toLowerCase()}`),
      }));
    },

    successStatus() {
      const statuses = {
        confirmOrder: 'Confirmed',
        confirmShipping: 'Confirmed Shipping',
        confirmOrderAndShipping: 'Confirmed Order & Shipping',
        createInvoice: 'Invoiced',
        cancelOrder: 'Cancelled',
      };

      return statuses[this.operation];
    },

    errorsByBill() {
      return this.errors.length ? this.errors.reduce((errors, item) => {
        const bill = (errors[item.bill_id] || []);
        bill.push(item);
        errors[item.bill_id] = bill;
        return errors;
      }, {}) : [];
    },
    selectedBillIds() {
      return this.selectedBills.map(item => item.id);
    },
  },

  mounted() {
    this.bills = this.selectedBills;
    this.form.setOptions('paymentDateOption', [{ id: 'payment_date', name: this.$t('static.bulkProcessBill.payment_date_options.selectDate') }, { id: 'due_date', name: this.$t('static.bulkProcessBill.payment_date_options.dueDate') }]);
    this.form.hideField('paymentDate');
    this.form.paymentDate = moment().format('YYYY-MM-DD');
    this.loading = true;
    Promise.all([
      organizationSettingsService.show(),
      paymentMethodService.all(),
    ]).then(() => {
      this.form.setOptions('paymentMethod', this.listPaymentMethods);
      this.loading = false;
    });
  },
  methods: {
    ...mapMutations({
      toggle: 'TOGGLE_MODAL',
      setModalContent: 'SET_MODAL_CONTENT',
      setModalProps: 'SET_MODAL_PROPS',
      unsetModalContent: 'UNSET_MODAL_CONTENT',
      setModalCloseOnClick: 'SET_MODAL_CLOSE_ON_CLICK',
      setModalShowClose: 'SET_MODAL_SHOW_CLOSE',
      setModalCloseOnPressEscape: 'SET_MODAL_CLOSE_ON_PRESS_ESCAPE',
    }),

    getBill(id) {
      return this.selectedBills.find(o => o.id === +id);
    },

    handleSubmit() {
      this.submitLoading = true;
      this.onSubmit(this.selected).then(() => {
        this.submitLoading = false;
        this.closeModal();
      }).catch((errors) => {
        if (errors.items) {
          this.$message({
            showClose: true,
            message: this.$t('static.bulkProductAdd.emptySelectionError'),
            type: 'error',
          });
        }
        this.submitLoading = false;
      });
    },
    confirm() {
      this.$confirm(`${this.$t('static.bills.index.confirmAction1')} ${this.$t(`static.bills.index.action.${this.operation}`)} ${this.$t('static.bills.index.confirmAction2')} ${this.bills.length} ${this.$t('static.bills.index.confirmAction3')}`, this.$t('static.bills.index.confirmActionTitle'), {
        confirmButtonText: this.$t('button.yes'),
        cancelButtonText: this.$t('button.no'),
        type: 'warning',
      }).then(() => {
        if (this.operation === 'markBilledAsPaid') {
          this.markBilledAsPaid();
        }
      }).catch(() => {});
    },
    markBilledAsPaid() {
      this.submitLoading = true;

      const paymentDates = this.bills.reduce((c, { id, paymentDate }) => {
        c[id] = paymentDate;
        return c;
      }, {});

      const amountPaids = this.bills.reduce((c, { id, amountDue, amountDueNum }) => {
        c[id] = amountDue !== '-' && !amountDue.includes('kr') ? amountDue : amountDueNum;
        return c;
      }, {});

      return billService.bulkMarkAsPaid(this.selectedBillIds, {
        payment_date_option: this.form.paymentDateOption,
        payment_date: this.form.paymentDate,
        payment_dates: paymentDates,
        amount_paids: amountPaids,
        payment_method: this.form.paymentMethod,
      }).then((data) => {
        this.showResult(data);
      }).catch((errors) => {
        this.submitLoading = false;
        let message = errors.data.message || '';
        if (errors.data.payment_date_option) {
          message = errors.data.payment_date_option[0];
        }
        if (errors.data.payment_date) {
          message = errors.data.payment_date[0];
        }
        if (message) {
          this.$message({
            showClose: true,
            message,
            customClass: 'error',
          });
        }
      });
    },

    showResult(data) {
      this.submitted = true;
      this.submitLoading = false;
      if (data.errors.length) {
        this.errors = data.errors;
      }
    },

    /*
     * Closes the modal
     */
    closeModal() {
      if (this.submitted) {
        this.onSubmit();
      }
      this.toggle(false);
      this.unsetModalContent();
    },
  },
  watch: {
    'form.paymentDateOption': function (paymentDateOption) {
      if (paymentDateOption === 'payment_date') {
        this.form.showField('paymentDate');
        this.form.paymentDate = '';
      } else {
        this.bills = this.bills.map(p =>
          ({ ...p, paymentDate: p.dueDate })
        );
        this.form.hideField('paymentDate');
      }
    },
    'form.paymentDate': function (paymentDate) {
      this.bills = this.bills.map(p =>
          ({ ...p, paymentDate })
        );
    },
  },
  components: {
    VForm: require('./../../components/form/form.vue'),
    VMessage: require('./../../components/form/message/message.vue'),
  },
};



// WEBPACK FOOTER //
// ./src/app/components/bulkProcessBillModal/bulkProcessBillModal.js